export interface Pdf417ScannerSession {
  stop: () => void;
  setTorch?: (enabled: boolean) => Promise<void>;
}

export interface Pdf417ScannerCallbacks {
  onDecode: (payload: string) => void;
  onError: (message: string) => void;
  onStarted: (status: string) => void;
}

const cameraConstraints: MediaStreamConstraints = {
  video: {
    facingMode: { ideal: "environment" },
    width: { ideal: 1280 },
    height: { ideal: 720 }
  },
  audio: false
};

export async function startPdf417Scanner(video: HTMLVideoElement, callbacks: Pdf417ScannerCallbacks): Promise<Pdf417ScannerSession> {
  if (!navigator.mediaDevices?.getUserMedia) {
    callbacks.onError("Camera access is not available in this browser.");
    return { stop: () => undefined };
  }

  let reader: { decodeFromCanvas: (canvas: HTMLCanvasElement) => { getText: () => string } } | undefined;
  let stream: MediaStream | undefined;
  let scanTimer: number | undefined;
  let completed = false;

  try {
    stream = await navigator.mediaDevices.getUserMedia(cameraConstraints);
    video.muted = true;
    video.playsInline = true;
    video.autoplay = true;
    video.srcObject = stream;
    await waitForVideoReady(video);
    await video.play();
    callbacks.onStarted(`${video.videoWidth} x ${video.videoHeight}`);

    const [{ BrowserPDF417Reader }, { DecodeHintType }] = await Promise.all([import("@zxing/browser"), import("@zxing/library")]);
    const hints = new Map([[DecodeHintType.TRY_HARDER, true]]);
    reader = new BrowserPDF417Reader(hints);
    const canvas = document.createElement("canvas");
    scanTimer = window.setInterval(() => {
      if (completed || !reader) return;
      if (!video.videoWidth || !video.videoHeight) return;
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const context = canvas.getContext("2d", { willReadFrequently: true });
      if (!context) return;
      context.drawImage(video, 0, 0, canvas.width, canvas.height);
      try {
        const result = reader.decodeFromCanvas(canvas);
        completed = true;
        stopScanner(scanTimer, stream, video);
        callbacks.onDecode(result.getText());
      } catch {
        return;
      }
    }, 260);
  } catch (error) {
    stopScanner(scanTimer, stream, video);
    callbacks.onError(cameraErrorMessage(error));
  }

  return {
    stop: () => {
      completed = true;
      stopScanner(scanTimer, stream, video);
    },
    setTorch: getTorchControl(stream)
  };
}

function waitForVideoReady(video: HTMLVideoElement): Promise<void> {
  if (video.readyState >= HTMLMediaElement.HAVE_METADATA && video.videoWidth > 0) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const timeout = window.setTimeout(() => {
      cleanup();
      reject(new Error("Camera preview timed out."));
    }, 6000);
    const cleanup = () => {
      window.clearTimeout(timeout);
      video.removeEventListener("loadedmetadata", handleReady);
      video.removeEventListener("canplay", handleReady);
      video.removeEventListener("error", handleError);
    };
    const handleReady = () => {
      if (!video.videoWidth) return;
      cleanup();
      resolve();
    };
    const handleError = () => {
      cleanup();
      reject(new Error("Camera preview failed."));
    };
    video.addEventListener("loadedmetadata", handleReady);
    video.addEventListener("canplay", handleReady);
    video.addEventListener("error", handleError);
  });
}

function stopScanner(scanTimer: number | undefined, stream: MediaStream | undefined, video: HTMLVideoElement) {
  if (scanTimer !== undefined) window.clearInterval(scanTimer);
  stream?.getTracks().forEach((track) => track.stop());
  if (video.srcObject === stream) video.srcObject = null;
}

function getTorchControl(stream: MediaStream | undefined): ((enabled: boolean) => Promise<void>) | undefined {
  const track = stream?.getVideoTracks()[0];
  const capabilities = track?.getCapabilities() as MediaTrackCapabilities & { torch?: boolean };
  if (!track || !capabilities?.torch) return undefined;
  return (enabled: boolean) => track.applyConstraints({ advanced: [{ torch: enabled } as MediaTrackConstraintSet] });
}

function cameraErrorMessage(error: unknown): string {
  if (error instanceof DOMException && (error.name === "NotAllowedError" || error.name === "SecurityError")) {
    return "Camera access was denied. Allow camera permission and try again.";
  }
  if (error instanceof DOMException && error.name === "NotFoundError") return "No camera was found on this device.";
  if (error instanceof DOMException && error.name === "NotReadableError") return "Camera is already in use. Close other camera apps and try again.";
  return "Unable to start the camera. Check browser permissions and try again.";
}
